/**
 * The simple pendulum, with and without the small-angle approximation.
 *
 *   T₀ = 2π √(L / g)                      small-angle period (θ₀ → 0)
 *   T  = T₀ · 2K(sin(θ₀/2)) / π           exact period at amplitude θ₀
 *      = T₀ / AGM(1, cos(θ₀/2))           the same, by the arithmetic–geometric mean
 *      ≈ T₀ (1 + θ₀²/16 + 11θ₀⁴/3072 + …)
 *
 * `K` is the complete elliptic integral of the first kind. The AGM converges
 * quadratically — a handful of iterations reach machine precision — so no elliptic
 * integral library is needed. A point-mass bob on a massless, inextensible string, no
 * drag, is assumed. SI units; angles in radians unless the name says degrees.
 */

export type PendulumTarget = "period" | "length" | "gravity";

export type PendulumError =
  | "missing"
  | "non-positive"
  | "amplitude-range"
  | "not-finite";

const AGM_TOLERANCE = 1e-15;
const AGM_MAX_ITERATIONS = 40;

/** Arithmetic–geometric mean of two positive numbers. */
export function agm(a: number, b: number): number {
  let x = a;
  let y = b;
  for (
    let i = 0;
    i < AGM_MAX_ITERATIONS && Math.abs(x - y) > AGM_TOLERANCE * x;
    i++
  ) {
    [x, y] = [(x + y) / 2, Math.sqrt(x * y)];
  }
  return (x + y) / 2;
}

/** T / T₀ for an amplitude in radians, 0 < θ₀ < π. Exactly 1 as θ₀ → 0. */
export function amplitudeFactor(amplitude: number): number {
  return 1 / agm(1, Math.cos(amplitude / 2));
}

/** The first `terms` terms of 1 + θ²/16 + 11θ⁴/3072 + 173θ⁶/737280 (in radians). */
export function seriesFactor(amplitude: number, terms: 1 | 2 | 3 | 4): number {
  const coefficients = [1, 1 / 16, 11 / 3072, 173 / 737_280];
  let sum = 0;
  for (let n = 0; n < terms; n++) {
    sum += coefficients[n] * amplitude ** (2 * n);
  }
  return sum;
}

export function smallAnglePeriod(length: number, gravity: number): number {
  return 2 * Math.PI * Math.sqrt(length / gravity);
}

export interface PendulumInputs {
  length: number | null;
  gravity: number | null;
  /** The period as measured or wanted, at the given amplitude. */
  period: number | null;
  /** Release angle in degrees, or null when not given. */
  amplitudeDegrees: number | null;
  /** Apply the large-angle correction; when false T = T₀. */
  correct: boolean;
}

export interface PendulumSolution {
  length: number;
  gravity: number;
  period: number;
  smallAnglePeriod: number;
  /** T / T₀. */
  factor: number;
  frequency: number;
  amplitudeDegrees: number | null;
  /** Speed at the bottom of the swing, m/s — null without an amplitude. */
  maxSpeed: number | null;
}

function positive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

const NEEDS: Record<PendulumTarget, readonly (keyof PendulumInputs)[]> = {
  period: ["length", "gravity"],
  length: ["gravity", "period"],
  gravity: ["length", "period"],
};

export function solvePendulum(
  inputs: PendulumInputs,
  target: PendulumTarget
): { ok: true; value: PendulumSolution } | { ok: false; error: PendulumError } {
  for (const key of NEEDS[target]) {
    if (inputs[key] === null) {
      return { ok: false, error: "missing" };
    }
    if (!positive(inputs[key] as number | null)) {
      return { ok: false, error: "non-positive" };
    }
  }
  const degrees = inputs.amplitudeDegrees;
  if (inputs.correct && degrees === null) {
    return { ok: false, error: "missing" };
  }
  if (degrees !== null && !(degrees > 0 && degrees < 180)) {
    return { ok: false, error: "amplitude-range" };
  }
  const radians = degrees === null ? null : (degrees * Math.PI) / 180;
  const factor =
    inputs.correct && radians !== null ? amplitudeFactor(radians) : 1;
  const solution = assemble(inputs, target, factor, radians);
  const numbers = Object.values(solution).filter(
    (item): item is number => typeof item === "number"
  );
  if (!numbers.every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: { ...solution, amplitudeDegrees: degrees } };
}

function assemble(
  inputs: PendulumInputs,
  target: PendulumTarget,
  factor: number,
  radians: number | null
): Omit<PendulumSolution, "amplitudeDegrees"> {
  const FOUR_PI_SQUARED = 4 * Math.PI ** 2;
  let length = inputs.length ?? 0;
  let gravity = inputs.gravity ?? 0;
  let period = inputs.period ?? 0;
  if (target === "period") {
    period = smallAnglePeriod(length, gravity) * factor;
  } else if (target === "length") {
    length = (gravity * (period / factor) ** 2) / FOUR_PI_SQUARED;
  } else {
    gravity = (FOUR_PI_SQUARED * length) / (period / factor) ** 2;
  }
  return {
    length,
    gravity,
    period,
    smallAnglePeriod: period / factor,
    factor,
    frequency: 1 / period,
    maxSpeed:
      radians === null
        ? null
        : Math.sqrt(2 * gravity * length * (1 - Math.cos(radians))),
  };
}
